import { useCallback, useEffect, useRef } from "react";
import { useDonationFormStore } from "@/stores";
import { useNavigate } from "@/hooks/useNavigate";
import { usePayment } from "@/payment/usePayment";
import {
  completedDonationFromReplacedSession,
  isSessionVerdictError,
  isUnknownOutcomeError,
  replacedSessionRefusal,
} from "@/payment/paymentError";
import { CURRENCY, PAYMENT_SESSION_REPLACEMENT_TIMEOUT_MS } from "@/config";
import { withTimeout } from "@/utils/with-timeout";
import { trackError } from "@/utils/error-monitoring";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { Screen } from "@/types/Navigation";
import type { PaymentSession } from "@/payment/types";
import type { SubmissionSuccessResponse } from "@/types/Payment";
import type { SubmissionError } from "./submissionError";

type UsePaymentSessionSyncArgs = {
  formId: number;
  /**
   * Must keep its identity across renders: it is a dependency of the pre-warm
   * debounce, and a new function on every render would cancel the timer before
   * it ever fires.
   */
  onRefused: (error: SubmissionError) => void;
};

const PREWARM_DEBOUNCE_MS = 300;

// Refresh a session this close to expiry before confirming on it.
const SESSION_EXPIRY_MARGIN_MS = 60_000;

// Keeps the payment session in step with the selected amount: pre-warms it on
// the Amount screen (so the Continue click doesn't pay the Braintree
// clientToken round-trip) and recreates it when the amount changes later —
// e.g. via the summary-sidebar pencil edit on the details screen. The charge
// follows the session amount, never the displayed one (#1065), so a session
// must not outlive an amount edit.
export function usePaymentSessionSync({
  formId,
  onRefused,
}: UsePaymentSessionSyncArgs) {
  const { session, recreateSession, createSession } = usePayment();
  const { screen, completeSubmission } = useNavigate();
  const {
    donationAmount,
    isEditingCustomAmount,
    clearPersistedDonationAmount,
  } = useDonationFormStore();

  const prewarmAmountRef = useRef<number | null>(null);
  const prewarmPromiseRef = useRef<Promise<boolean> | null>(null);
  const pendingPrewarmRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The session whose confirm outcome is unknown: the backend may have
  // charged on it, so it must survive until the backend answers *on it* —
  // not until any response arrives. A Turnstile 422, a 429 or any status a
  // new middleware may add say nothing about the session and keep it; a
  // session verdict (410 failed, a decline) releases it. A ref, not state:
  // the retry clears the banner before it runs.
  const unresolvedSessionIdRef = useRef<string | null>(null);

  const isSessionUnresolved = () =>
    session?.session_id != null &&
    unresolvedSessionIdRef.current === session.session_id;

  const releaseUnresolvedSession = () => {
    unresolvedSessionIdRef.current = null;
  };

  // The other two refusals leave the donor on the form with a payment that
  // may already have gone through. Turn them into the banner !1102 built for
  // exactly this, instead of an unhandled rejection and a dead Continue.
  const showRefusalBanner = useCallback(
    (maybeError: unknown): boolean => {
      const refusal = replacedSessionRefusal(maybeError);

      if (!refusal) return false;

      onRefused(
        refusal === "charged_pending_review"
          ? "charged-pending-review"
          : "unknown-outcome",
      );
      return true;
    },
    [onRefused],
  );

  // Every way a charge is known to have been stored ends here: the confirm
  // answer, the status poll after a lost answer, or a refused replacement.
  const settleDonation = useCallback(
    (completed: SubmissionSuccessResponse) => {
      dispatchEvent(EventName.PURCHASE, completed.datalayer);
      clearPersistedDonationAmount();
      unresolvedSessionIdRef.current = null;
      completeSubmission(completed);
    },
    [clearPersistedDonationAmount, completeSubmission],
  );

  // The backend refused to hand out a new session because the one being
  // replaced had already been charged and stored. That is not an error: the
  // donation went through, so show the thank-you page the donor never saw
  // instead of asking them to pay again.
  const settleIfAlreadyDonated = useCallback(
    (maybeError: unknown): boolean => {
      const completed = completedDonationFromReplacedSession(maybeError);

      if (!completed) return false;

      settleDonation(completed);
      return true;
    },
    [settleDonation],
  );

  useEffect(() => {
    if (screen === Screen.Success) return;
    if (!donationAmount || donationAmount <= 0) return;
    if (prewarmAmountRef.current === donationAmount) return;
    // One session per keystroke or arrow step used up the payment rate limit
    // (#1196): wait until the donor leaves the field.
    if (isEditingCustomAmount) return;
    // Inlined rather than isSessionUnresolved(): `session` is already a dep.
    if (session && unresolvedSessionIdRef.current === session.session_id) {
      return;
    }

    const handle = setTimeout(() => {
      prewarmAmountRef.current = donationAmount;
      const input = {
        form_id: formId,
        amount: donationAmount,
        currency: CURRENCY,
      };
      const open = session ? recreateSession : createSession;
      // Bounded like every other session request: a warm that never answers
      // would otherwise hold openSessionForAmount, and with it the Continue
      // button, until the browser gives up on the socket.
      const promise: Promise<boolean> = withTimeout(
        (signal) => open(input, signal),
        PAYMENT_SESSION_REPLACEMENT_TIMEOUT_MS,
      ).then(
        // Undefined means a newer request superseded this one.
        (opened) => opened !== undefined,
        (e: unknown) => {
          // Only a request that actually started supersedes this warm. A
          // cancelled debounce must leave the original failure recoverable.
          if (prewarmPromiseRef.current !== promise) return false;

          if (settleIfAlreadyDonated(e)) return false;
          if (showRefusalBanner(e)) return false;

          prewarmAmountRef.current = null;
          trackError(e as Error, { payload: { context: "prewarm", ...input } });
          // Resolving instead of rejecting lets openSessionForAmount fall
          // through to the blocking createSession rather than reject the
          // Continue click.
          return false;
        },
      );
      prewarmPromiseRef.current = promise;
    }, PREWARM_DEBOUNCE_MS);
    pendingPrewarmRef.current = handle;

    return () => clearTimeout(handle);
  }, [
    donationAmount,
    isEditingCustomAmount,
    screen,
    formId,
    session,
    createSession,
    recreateSession,
    settleIfAlreadyDonated,
    showRefusalBanner,
  ]);

  // Resolves "already-donated" when the thank-you page has taken over and the
  // caller must not navigate. A refusal resolves "ready" on purpose: its banner
  // lives on the details screen, so the donor has to land there to see it;
  // staying on Amount would look like a dead button.
  const openSessionForAmount = async (): Promise<
    "ready" | "already-donated"
  > => {
    // Leaving the amount field to press Continue schedules a warm that the
    // request below makes redundant.
    clearTimeout(pendingPrewarmRef.current);

    // `session` belongs to the render Continue was pressed in: a warm that
    // finishes while we wait opens a session this closure never sees.
    const warmedWhileWaiting = prewarmPromiseRef.current
      ? await prewarmPromiseRef.current
      : false;
    const hasSession = session != null || warmedWhileWaiting;

    if (hasSession && prewarmAmountRef.current === donationAmount) {
      return "ready";
    }

    // recreateSession when the outcome of this session is unknown: it sends
    // old_session_id, which is the only way the backend can tell that the
    // donor was already charged and refuse a second session. A plain
    // createSession would mint an unlinked one and let them pay twice.
    const open = isSessionUnresolved() ? recreateSession : createSession;

    // The same deadline as the other session requests: this runs under the
    // submission lock, and a stalled request would leave Continue dead.
    try {
      await withTimeout(
        (signal) =>
          open(
            {
              form_id: formId,
              amount: donationAmount ?? 0,
              currency: CURRENCY,
            },
            signal,
          ),
        PAYMENT_SESSION_REPLACEMENT_TIMEOUT_MS,
      );
    } catch (e: unknown) {
      if (settleIfAlreadyDonated(e)) return "already-donated";
      if (showRefusalBanner(e)) return "ready";
      throw e;
    }
    // Mark the amount as covered so the resync effect doesn't schedule a
    // redundant recreate right after landing on the details screen.
    prewarmAmountRef.current = donationAmount;
    return "ready";
  };

  // Replaces the session a failed confirmation consumed. Resolves false when
  // no replacement happened (the request failed or stalled past the
  // deadline): the failed session must not be retried, so the caller keeps
  // the donor on the error screen until a fresh session is available.
  const replaceFailedSession = async (): Promise<boolean> => {
    try {
      await withTimeout(
        (signal) =>
          recreateSession(
            {
              form_id: formId,
              amount: donationAmount ?? 0,
              currency: CURRENCY,
            },
            signal,
          ),
        PAYMENT_SESSION_REPLACEMENT_TIMEOUT_MS,
      );
      prewarmAmountRef.current = donationAmount;
      return true;
    } catch (sessionError) {
      prewarmAmountRef.current = null;
      trackError(sessionError as Error, {
        payload: { context: "payment-retry-session", form_id: formId },
      });
      return false;
    }
  };

  // Not `prewarmAmountRef` alone: it is null whenever the pre-warm failed —
  // likely on the flaky connection that produced the unknown outcome — and
  // that must never re-mint the session the backend can replay from.
  const needsReplacementBeforeRetry = () =>
    !isSessionUnresolved() && prewarmAmountRef.current !== donationAmount;

  // Replacing the session after an unanswered confirm is what turns a retry
  // into a second charge: the backend can only replay a result it stored
  // against the same session id.
  // A verdict on the unresolved session ("failed", a decline…) means the
  // backend answered on it without charging: the session can go.
  // `confirmedSessionId`, not `session`: a pre-confirm refresh can confirm on
  // a session this render has not seen yet.
  const trackUnknownOutcome = (
    serverError: unknown,
    confirmedSessionId: string,
  ): boolean => {
    if (isSessionVerdictError(serverError)) {
      unresolvedSessionIdRef.current = null;
    }

    const unknownOutcome =
      unresolvedSessionIdRef.current === confirmedSessionId ||
      isUnknownOutcomeError(serverError);

    if (unknownOutcome) {
      unresolvedSessionIdRef.current = confirmedSessionId;
    }

    return unknownOutcome;
  };

  // A tab can sleep for an hour, or the donor can spend time in 3DS or the
  // recurring-donation prompt, so check expiry at submission too. Never
  // refresh a session whose outcome is unknown: the retry must replay on it,
  // and the backend replays a confirmed session even after it expires.
  const sessionForConfirm = async (): Promise<PaymentSession> => {
    if (session && isSessionUnresolved()) return session;

    const expiresAt = Date.parse(session?.expires_at ?? "");
    if (session && expiresAt > Date.now() + SESSION_EXPIRY_MARGIN_MS) {
      return session;
    }

    let refreshed: PaymentSession | undefined;
    try {
      refreshed = await withTimeout(
        (signal) =>
          recreateSession(
            {
              form_id: formId,
              amount: donationAmount ?? 0,
              currency: CURRENCY,
            },
            signal,
          ),
        PAYMENT_SESSION_REPLACEMENT_TIMEOUT_MS,
      );
    } catch (refreshError) {
      // A refresh inside the safety margin can fail while the old session
      // is still usable. Recheck after the request in case it outlived the
      // remaining time. The server remains authoritative about its status.
      if (session && Number.isFinite(expiresAt) && expiresAt > Date.now()) {
        return session;
      }
      throw refreshError;
    }

    // An undefined result means a newer request superseded this one; never
    // fall back to an older session (which may have a different amount).
    if (!refreshed) throw new Error("Payment session was replaced");
    return refreshed;
  };

  // The refusals the pre-warm handles can also surface when the pre-confirm
  // refresh fails, if another tab already paid.
  const handleRefusedReplacement = (maybeError: unknown): boolean =>
    settleIfAlreadyDonated(maybeError) || showRefusalBanner(maybeError);

  return {
    sessionForConfirm,
    handleRefusedReplacement,
    openSessionForAmount,
    replaceFailedSession,
    needsReplacementBeforeRetry,
    trackUnknownOutcome,
    releaseUnresolvedSession,
    settleDonation,
  };
}
